import { PREFERRED_CALENDAR_VIEW, SHOW_MEMORIES_ON_HOURS } from 'constants/user';
import { union, uniq } from 'lodash-es';
import { DateTime } from 'luxon';
import type { HourDraftSeed } from 'redux/modules/hours.actions';
import {
  batchCreateHourDraft,
  clearHourDraft,
  createHourDraft,
  fetchHours,
} from 'redux/modules/hours.actions';
import { showSubscriptionNoticeModal } from 'redux/modules/subscriptions.actions';
import { fetchSuggestions } from 'redux/modules/suggestions.actions';
import { clearSelectEntryIds } from 'redux/modules/timeline.actions';
import { saveUserProperty } from 'redux/modules/user.actions';
import { getCurrentAccount, getHasHourStatesFeature } from 'redux/selectors/accounts';
import { getCalendarUserId, getListMemoriesOnHours } from 'redux/selectors/calendar';
import {
  getDates,
  getFilter,
  getRouteParameters,
  getUrlParameters,
} from 'redux/selectors/calendar.filter';
import { getHours, getHoursAsCards } from 'redux/selectors/hours';
import { getHourDraft } from 'redux/selectors/hours.draft.base';
import { getReportSelection } from 'redux/selectors/superreports';
import { getTeams, getUserLedUserIds } from 'redux/selectors/teams';
import { getUser, getUserId, getUserProperties, getUserRoleId } from 'redux/selectors/user';
import type { HourAnalyticsContext } from 'types/analytics';
import type { Thunk } from 'types/redux';
import { isSameMonth, isSameWeek } from 'utils/date-helper';
import debug from 'utils/debug';
import { isHourLocked } from 'utils/hours';
import { fromISO } from 'utils/luxon-helpers';
import { encodeUrlParams } from 'utils/url-params';
import type {
  CombinedParameters,
  Filter,
  FilterSpan,
  RouteParameters,
  UrlParameters,
} from './calendar.types';
import * as actions from './calendar.types';
import { CalendarViewMode, storeFilter } from './calendar.types';
import { fetchDayPropertiesSmartly, isDayLockedForUser, saveDayProperties } from './dayProperties';
import { getDayProperties } from './dayProperties.selectors';
import { getHourStates } from './hourStates';
import type { HourState } from './hourStates.schema';
import { batchSelectionActions } from './superreports.types';
import { getAssistantResponse } from './tic/agent/tic.ai.agent.actions';
import { getAiResponse } from './tic/agent/tic.ai.agent.slice';

export const setFilter =
  (applyFilter: Partial<CombinedParameters>): Thunk =>
  (dispatch, getState, history) => {
    const state = getState();
    const { span, ...applyRestUrlParameters } = applyFilter;

    const validatedSpan = span && ['day', 'week', 'month'].includes(span) ? span : undefined;

    const existingUrlParameters: UrlParameters = getUrlParameters(state); // from url or defaults

    const updatedUrlParams: UrlParameters = {
      ...existingUrlParameters,
      ...applyRestUrlParameters,
    };

    const existingRouteParameters: RouteParameters = getRouteParameters(state);
    const currentSpan = validatedSpan || existingRouteParameters.span;

    const applySpan: RouteParameters | undefined = validatedSpan
      ? { span: currentSpan }
      : undefined;

    history.push(`/calendar/${currentSpan}${encodeUrlParams(updatedUrlParams)}`);
    dispatch(
      storeFilter({
        ...applySpan,
        ...applyRestUrlParameters,
      })
    );
  };

export const goToDay =
  (date: string): Thunk =>
  dispatch => {
    const applyFilter: Partial<Filter> = {
      date,
      span: 'day',
    };
    dispatch(setFilter(applyFilter));
  };

export function setCalendarDate(date?: string): Thunk {
  const toDate = date || DateTime.now().toISODate();
  return dispatch => {
    dispatch(
      setFilter({
        date: toDate,
      })
    );
    dispatch(clearHourDraft());
  };
}

/** Shows or hides the Memory Inbox for a specific date */
export function toggleMemoryInbox(date?: string): Thunk {
  return dispatch => {
    // Memory filters rely on calendar's date
    if (date) {
      dispatch(setCalendarDate(date));
    } else {
      dispatch(clearSelectEntryIds());
    }

    dispatch(actions.setMemoryInboxVisibility(!!date));
  };
}

export function toggleHourMemoriesVisibility(): Thunk {
  return (dispatch, getState) => {
    const currentValue = getListMemoriesOnHours(getState());
    dispatch(saveUserProperty(SHOW_MEMORIES_ON_HOURS, !currentValue));
  };
}

export function loadHoursByFilter(key?: string): Thunk {
  return (dispatch, getState) => {
    const state = getState();
    const defaultUserId = getCalendarUserId(state);
    const filter = getFilter(state);
    const dates = getDates(state);
    const from = dates[0];
    const to = dates[dates.length - 1];
    // Todo:
    // Remember which users/days we have already loaded and exclude those userIds
    // from the request.
    const query = {
      since: from,
      upto: to,
      userId: filter.multiUserMode ? null : defaultUserId,
      userIds: filter.multiUserMode ? filter.userIds : null,
    };

    // Only fetch suggestions in solo user mode
    if (!filter.multiUserMode && filter.isActingAsSelf) {
      dispatch(fetchSuggestions({ from, to }));
    }

    return dispatch(fetchHours(query, { key }));
  };
}

export function loadDayPropertiesForCalendar(): Thunk {
  return (dispatch, getState) => {
    const dates = getDates(getState());
    const from = dates[0];
    const to = dates[dates.length - 1];
    dispatch(fetchDayPropertiesSmartly({ since: from, until: to }));
  };
}

export function setCalendarSpan(span: FilterSpan): Thunk {
  return (dispatch, getState) => {
    const state = getState();
    const { start_of_week: startOfWeek } = getCurrentAccount(state);
    const today = DateTime.now().toISODate();
    const filter = getFilter(state);
    // If "today" was visible from previous span, go to "today".
    const switchingFromMonth = filter.span === 'month';
    const switchingFromWeek = filter.span === 'week';
    const shouldSetTodayAsDate =
      (switchingFromWeek && isSameWeek(filter.date, today, startOfWeek)) ||
      (switchingFromMonth && isSameMonth(filter.date, today));

    dispatch(
      setFilter({
        span,
        date: shouldSetTodayAsDate ? today : filter.date,
      })
    );

    dispatch(saveUserProperty(PREFERRED_CALENDAR_VIEW, span));
  };
}

export function goPreviousPeriod(): Thunk {
  return (dispatch, getState) => {
    // getFilter changes span based on multiuser mode, etc, so read from there:
    const filter = getFilter(getState());
    dispatch(
      setFilter({
        date: fromISO(filter.date)
          .minus({ [filter.span]: 1 })
          .toISODate(),
      })
    );
    dispatch(clearHourDraft());
  };
}

export function goNextPeriod(): Thunk {
  return (dispatch, getState) => {
    // getFilter changes span based on multiuser mode, etc, so read from there:
    const filter = getFilter(getState());
    dispatch(
      setFilter({
        date: fromISO(filter.date)
          .plus({ [filter.span]: 1 })
          .toISODate(),
      })
    );
    dispatch(clearHourDraft());
  };
}

export function setCalendarUserIds(userIds: Array<number> | number) {
  const value = Array.isArray(userIds) ? userIds : [userIds];
  return setFilter({
    userIds: value,
  });
}

export function setCalendarTeamIds(teamIds: Array<number> | number): Thunk {
  return (dispatch, getState) => {
    const state = getState();
    const value: Array<number> = Array.isArray(teamIds) ? teamIds : [teamIds];

    const shouldSelectUsersOnZeroTeams = value.length === 1 && value[0] === 0;

    if (shouldSelectUsersOnZeroTeams) {
      debug.warn('@todo: We should select only users without a team here');
    }

    const userIds = getTeams(state).reduce((prev: Array<number>, team): Array<number> => {
      if (team.id != null && value.includes(team.id)) {
        return [...prev, ...team.users.map(u => u.user_id)];
      }
      return prev;
    }, []);

    dispatch(
      setFilter({
        userIds: uniq(userIds),
      })
    );
  };
}

export function setCalendarViewMode(mode: CalendarViewMode): Thunk {
  return (dispatch, getState) => {
    const state = getState();
    const filter = getFilter(state);
    const { features } = getCurrentAccount(state);

    // If multi view isn't available, abort with a subscription upgrade nag
    // Might want to move this to a inline upsell, ref https://app.clubhouse.io/timelyapp/story/9296/design-company-view-needs-better-design-upsell
    if (!features.company_view && mode === 'multi') {
      dispatch(showSubscriptionNoticeModal('featureCompanyViewRequiresUpgrade'));
      return;
    }

    // If switching from multi -> solo with > 1 selected user,
    // change back to viewing the authenticated user.
    const isSwitchingFromMultiUser = filter.multiUserMode;
    const wasViewingManyUsers = filter.userIds.length > 1;

    if (mode === CalendarViewMode.SOLO && isSwitchingFromMultiUser && wasViewingManyUsers) {
      const authenticatedUserId = getUserId(state);
      dispatch(
        setFilter({
          multiUserMode: mode !== CalendarViewMode.SOLO,
          userIds: [authenticatedUserId],
        })
      );
    } else {
      dispatch(
        setFilter({
          multiUserMode: mode !== CalendarViewMode.SOLO,
        })
      );
    }
  };
}

export function selectHourIds(hourIds: Array<number> = []) {
  return actions.setSelectionProperty({
    key: 'hourIds',
    value: hourIds,
  });
}

export function goToTodayKeyboardShortcut(): Thunk {
  return (dispatch, getState) => {
    const state = getState();
    const userId = getUserId(state);

    dispatch(
      setFilter({
        date: DateTime.now().toISODate(),
        userIds: [userId],
        span: 'day',
      })
    );
  };
}

/**
 * Generic "perform action on day".
 *
 * If no userId is passed (only required for multiuser views), we'll act as the calendarUserId.
 *
 * Dev note:
 * While I know we're not loving the "magic actions", centralizing this in one place allows
 * - Reuse in Calendar & MultiUserCalendar with a single import
 * - Avoids passing down 4 (for now) action creators through Calendar -> DayHeader and Day (for inline DayHeader)
 */
export interface HandleDayAction {
  day: string;
  action:
    | 'clone'
    | 'move'
    | 'batchCreateHourDraft'
    | 'lock'
    | 'lockAll'
    | 'unlockAll'
    | 'unlock'
    | 'acceptAllSuggestions'
    | 'rejectAllSuggestions'
    | 'seeReportOnDay'
    | 'toggleShowMemories';
  userId?: number | null;
}
export function execDayAction({ day, action, userId }: HandleDayAction): Thunk {
  return (dispatch, getState) => {
    const state = getState();
    const actualUserId = userId || getCalendarUserId(state);
    let { userIds } = getFilter(state);
    if (userIds == null || userIds.length === 0) {
      userIds = [actualUserId];
    }
    switch (action) {
      case 'move': {
        const hourIds = getHoursAsCards(state)
          .filter(hour => hour.day === day && hour.user_id === actualUserId)
          .map(hour => hour.id);
        dispatch(actions.setBatchHourModalMode(actions.BatchHourModalMode.Move));
        dispatch(selectHourIds(hourIds));
        break;
      }
      case 'clone': {
        const hourIds = getHoursAsCards(state)
          .filter(hour => hour.day === day && hour.user_id === actualUserId)
          .map(hour => hour.id);
        dispatch(actions.setBatchHourModalMode(actions.BatchHourModalMode.Copy));
        dispatch(selectHourIds(hourIds));
        break;
      }
      case 'batchCreateHourDraft': {
        dispatch(batchCreateHourDraft(day));
        break;
      }
      case 'lock': {
        dispatch(saveDayProperties({ user_ids: [actualUserId], dates: [day], locked: true }));
        break;
      }
      case 'unlock': {
        dispatch(saveDayProperties({ user_ids: [actualUserId], dates: [day], locked: false }));
        break;
      }
      case 'lockAll': {
        dispatch(
          saveDayProperties({
            user_ids: userIds,
            dates: [day],
            locked: true,
          })
        );
        break;
      }
      case 'unlockAll': {
        dispatch(
          saveDayProperties({
            user_ids: userIds,
            dates: [day],
            locked: false,
          })
        );
        break;
      }
      case 'toggleShowMemories': {
        const showMemoriesOnHours = !!getUserProperties(state).show_memories_on_hours;
        dispatch(saveUserProperty(SHOW_MEMORIES_ON_HOURS, !showMemoriesOnHours));
        break;
      }
      default:
        debug.warn('Not yet implemented!');
        break;
    }
  };
}

export function createHourDraftShortcut(context: HourAnalyticsContext): Thunk {
  return (dispatch, getState) => {
    const state = getState();
    const hasDraft = getHourDraft(state) != null;
    if (hasDraft) {
      return;
    }
    const user = getUser(state);
    const seed: HourDraftSeed = { day: DateTime.now().toISODate(), user };
    dispatch(createHourDraft(context, seed));
    dispatch(
      setFilter({
        date: seed.day,
        userIds: [user.id],
      })
    );
  };
}

export function selectAllHours(options = { includeLocked: false }): Thunk {
  return (dispatch, getState) => {
    const state = getState();
    const states = getHourStates(state);
    const userRoleId = getUserRoleId(state);
    const hasHourStatesFeature = getHasHourStatesFeature(state);
    const userLedUserIds = getUserLedUserIds(state);
    const dayProperties = getDayProperties(state);
    const hourIds = getHours(state)
      .filter(hour => {
        if (isDayLockedForUser(hour.user.id, dayProperties[hour.day])) {
          return !!options.includeLocked;
        }

        if (!hasHourStatesFeature) {
          return options.includeLocked || !hour.locked;
        }

        const userIsTeamLead = userLedUserIds.has(hour.user.id);
        return options.includeLocked || !isHourLocked(hour, { states, userRoleId, userIsTeamLead });
      })
      .map(hour => hour.id);

    dispatch(
      batchSelectionActions.setSelection({
        selectionKey: 'hourIds',
        value: hourIds,
      })
    );
  };
}

export interface SelectAllHoursForDayParams {
  day: string;
  userId: number | null;
  select: boolean;
}
export function selectAllHoursForDay({ day, userId, select }: SelectAllHoursForDayParams): Thunk {
  return (dispatch, getState) => {
    const state = getState();
    const states = getHourStates(state);
    const dayProperties = getDayProperties(state);
    const userRoleId = getUserRoleId(state);
    const hasHourStatesFeature = getHasHourStatesFeature(state);
    const userLedUserIds = getUserLedUserIds(state);
    const hours = getHours(state);
    const hourIdsOnDay = hours.filter(h => h.day === day).map(h => h.id);
    const selectableHourIds = hours
      .filter(hour => {
        const isDayLocked = isDayLockedForUser(hour.user.id, dayProperties[hour.day]);
        const isSelectable =
          hour.day === day && !isDayLocked && (userId == null || hour.user.id === userId);
        if (!hasHourStatesFeature) {
          return isSelectable && !hour.locked;
        }
        const userIsTeamLead = userLedUserIds.has(hour.user.id);
        return isSelectable && !isHourLocked(hour, { states, userRoleId, userIsTeamLead });
      })
      .map(hour => hour.id);
    const existingSelection = getReportSelection(state).hourIds;
    const existingSelectionOnDay = existingSelection.filter(id => hourIdsOnDay.includes(id));

    let newSelection: Array<number> = [];
    if (select) {
      if (existingSelectionOnDay.length) {
        if (selectableHourIds.length === hourIdsOnDay.length) {
          newSelection = existingSelection.filter(id => !selectableHourIds.includes(id));
        } else {
          newSelection = union(existingSelection, selectableHourIds);
        }
      } else {
        newSelection = union(existingSelection, selectableHourIds);
      }
    } else {
      newSelection = existingSelection.filter(id => !selectableHourIds.includes(id));
    }

    dispatch(
      batchSelectionActions.setSelection({
        selectionKey: 'hourIds',
        value: newSelection,
      })
    );
    dispatch(batchSelectionActions.setSelection({ selectionKey: 'rangeStartHourId', value: null }));
    dispatch(batchSelectionActions.setSelection({ selectionKey: 'rangeEndHourId', value: null }));
  };
}

export function selectHoursByState(hourState: HourState): Thunk {
  return (dispatch, getState) => {
    const state = getState();
    const hourIds = getHours(state)
      .filter(hour => (hour.state?.id ?? 0) === hourState.id)
      .map(hour => hour.id);
    dispatch(
      batchSelectionActions.setSelection({
        selectionKey: 'hourIds',
        value: hourIds,
      })
    );
  };
}

export function sendMessageToAssistant(message: string): Thunk {
  return (dispatch, getState) => {
    const ai_response = getAiResponse(getState());
    dispatch(getAssistantResponse({ message, session_id: ai_response?.session_id }));
  };
}
